import SimplexNoise from 'simplex-noise'

export const splitPath = path => {
  if (!path) return [];
  const [head, ...tail] = path.split("/");
  if (!head) return tail;
  else return [head, ...tail];
}

export const isCollectionReference = path => {
  return splitPath(path).length % 2 === 1;
}

/**
 * Mounts a context, that is, merges a template with item data in order to provide
 * the full context information, both names, structure and grade, goals, actions.
 * @param {*} items 
 * @param {*} template 
 */
export const mountContext = (context_doc, items, template) => {
  const rndGradeMin = 4
  const rndGradeMax = 8
  const rndGoalMin = 7
  const rndGoalMax = 9
  const categories = template.categories;
  const cat_arr = [];
  const r1 = 0.55;
  const r2 = 0.7;
  const noise = new SimplexNoise('5');

  const nItems = Object.values(template.categories)
    .reduce((sum, cat) => sum + Object.values(cat.items).length, 0);

  let i = 0;
  for (const cat_id in categories) {
    const item_arr = [];
    for (const item_id in categories[cat_id].items) {
      const ang        = i++ * 2 * Math.PI / nItems;
      const noiseVal   = (noise.noise2D(Math.cos(ang)*r1, Math.sin(ang)*r1) + 1) / 2;
      const noiseVar   = (noise.noise2D(Math.cos(ang)*r2, Math.sin(ang)*r2) + 1) / 2;
      const rndGrade   = Math.floor(noiseVal * (rndGradeMax - rndGradeMin + 1)) + rndGradeMin;
      const rndGoalBot = Math.max(rndGoalMin, rndGrade);
      const rndGoalTop = Math.max(rndGoalMax, rndGrade);
      const rndGoal    = Math.floor(noiseVar * (rndGoalTop - rndGoalBot + 1)) + rndGoalBot;
      item_arr.push({
        id: item_id,
        ...categories[cat_id].items[item_id],
        grade: rndGrade,
        goal:  rndGoal,
        ...items[`/default/${cat_id}/${item_id}`]
      });
    }
    cat_arr.push({
      id: cat_id,
      ...categories[cat_id],
      items: item_arr
    });
  }
  return {
    name: template.name,
    rating_date: context_doc.rating_date ?? today(),
    goal_deadline: context_doc.goal_deadline ?? tomorow(),
    categories: cat_arr
  };
};

export const unmountContext = context => {
  const items = {};
  for (const category of context.categories) {
    for (const item of category.items) {
      items[item.doc_id] = {
        grade: item.grade,
        goal: item.goal,
        path: item.path
      }
    }
  }
  return items;
};

const getItemsDict = template => {
  const categories = template.categories;
  const item_dict = {};
  for (const cat_id in categories) {
    const cat = categories[cat_id];
    for (const item_id in cat.items) {
      const item = cat.items[item_id];
      item_dict[`/${template.id}/${cat_id}/${item_id}`] = {
        category_name: cat.name,
        item_name: item.name,
        color: cat.color
      };
    }
  }
  return item_dict;
}

export const mountNotes = (notes, template) => {
  const item_dict = getItemsDict(template);
  const mounted = [];
  for (const note_id in notes) {
    const note = notes[note_id];
    let category_names = [], item_names = [], colors = [];
    note.item_paths.forEach(item_path => {
      let item;
      if (typeof item_path === "string") item = item_dict[item_path];
      else item = item_dict[item_path.path];
      
      category_names.push(item.category_name);
      item_names.push(item.item_name);
      colors.push(item.color);
    });
    mounted.push({
      category_names,
      item_names,
      colors,
      ...note
    })
  }
  return mounted;
};

export const cleanupObj = obj => {
  Object.keys(obj)
    .forEach(key => obj[key] ?? delete obj[key]);
  
  return obj;
}

export const pathTail = path => {
  return splitPath(path).splice(1).join("/");
}

export const pathHead = path => {
  const parts = splitPath(path);
  if (parts.length === 0) return null;
  return parts[0];
}

export const payloadPathTail = payload => {
  return { ...payload, path: pathTail(payload.path) };
}

export const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
}

export const tomorow = () => {
  const date = new Date();
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${(date.getDate() + 1).toString().padStart(2, "0")}`;
}

/**
 * Joins strings into a path with the '/' separator.
 * @param  {...String} parts 
 * @returns {String} The joined path with no leading '/'.
 */
export const pathJoin = (...parts) => parts.join("/");

/**
 * Converts from minutes from start of day to locale time.
 * @param {number} time 
 * @returns {String} The locale time string
 */
export function minutesToLocaleTime(time) {
  const hours = Math.floor(time / 60).toString().padStart(2, '0');
  const minutes = (time % 60).toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}